import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {ACTION, CHANGED} from 'external/dev_april_corgi+/js/emu/events';

import {TableType, TableValue} from './types/data';

import {
  State,
  TableEditorController,
  serialize,
} from './table_editor_controller';
import {Button, Input} from './widgets';

export function makeTableEditor({
  type,
  value,
  onUpdate,
}: {
  type: TableType;
  value: TableValue;
  onUpdate: (value: TableValue) => void;
}) {
  return <TableEditor type={type} value={value} onUpdate={onUpdate} />;
}

export function TableEditor(
  {
    type,
    value,
    onUpdate,
  }: {
    type: TableType;
    value: TableValue;
    onUpdate: (value: TableValue) => void;
  },
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    state = {
      mode: 'table',
      csv: '',
      csvError: undefined,
      tableError: undefined,
      tableUpdate: true,
      value,
    };
  }

  const keys = [...state.value.values.keys()];
  const columns = [...state.value.values.values()];
  return (
    <>
      <div
        js={corgi.bind({
          controller: TableEditorController,
          args: {type, onUpdate},
          events: {render: 'wakeup'},
          state: [state, updateState],
        })}
        className="bg-neutral-800 max-h-[inherit] overflow-y-auto p-4 rounded"
      >
        <div className="flex">
          <div
            className={
              'cursor-pointer px-2 rounded-t select-none ' +
              (state.mode === 'table' ? 'bg-neutral-500' : 'bg-neutral-700')
            }
            unboundEvents={{click: 'showTable'}}
          >
            Table
          </div>
          <div
            className={
              'cursor-pointer px-2 rounded-t select-none ' +
              (state.mode === 'csv' ? 'bg-neutral-500' : 'bg-neutral-700')
            }
            unboundEvents={{click: 'showCsv'}}
          >
            CSV
          </div>
        </div>
        <div className="border border-neutral-500 py-4 rounded">
          <div className={state.mode === 'csv' ? 'block' : 'hidden'}>
            <textarea
              className="h-64 w-[48rem]"
              unboundEvents={{focusout: 'csvChanged', keyup: 'csvChanged'}}
              value={state.csv}
            />
            <div>{state.csvError ?? ''}</div>
          </div>
          <div className={state.mode === 'table' ? 'block' : 'hidden'}>
            <table className="min-w-[33vw]">
              <thead>
                <tr>
                  {keys.map(k => (
                    <td className="font-bold px-2">{k}</td>
                  ))}
                  <td></td>
                </tr>
              </thead>
              <tbody>
                {columns[0].map((_, i) => (
                  <tr className="group odd:bg-neutral-700/30" data-row={i}>
                    {columns.map((c, j) => (
                      <td className="px-2" data-col={j}>
                        <div
                          className="inline-block"
                          unboundEvents={{corgi: [[CHANGED, 'tableChanged']]}}
                        >
                          <Input
                            className="w-16"
                            value={
                              state.tableUpdate ? serialize(c[i]) : undefined
                            }
                          />
                        </div>
                      </td>
                    ))}
                    <td className="px-2">
                      <Button
                        className={
                          'bg-red-400 invisible pointer-events-none ' +
                          'active:bg-red-500 hover:bg-red-400/90 ' +
                          'group-hover:visible group-hover:pointer-events-auto'
                        }
                        unboundEvents={{corgi: [[ACTION, 'rowDelete']]}}
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <Button
                      className="m-2"
                      unboundEvents={{corgi: [[ACTION, 'tableAddRow']]}}
                    >
                      + Add
                    </Button>
                  </td>
                  <td colspan="100%"></td>
                </tr>
              </tbody>
            </table>
            <div className="ms-2">{state.tableError ?? ''}</div>
          </div>
        </div>
        <div className="font-bold mt-4">Input material amounts</div>
        <table className="text-center w-full">
          <thead>
            <tr>
              {type.columnLabels
                .filter(l => l !== 'Label')
                .map(l => (
                  <td className="px-2">{l}</td>
                ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-neutral-700/30">
              {type.columnLabels
                // We map before the filter to ensure i doesn't get messed up
                .map((l, i) => [
                  l,
                  <td className="px-2">{serialize(type.defaults[i])}</td>,
                ])
                .filter(([l, _]) => l !== 'Label')
                .map(([_, td]) => td)}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
